import { useState, useContext, useEffect, useRef, useCallback } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { CircularProgress, TextField } from "@mui/material";
import { GlobalStateContext } from "../../../components/context/GlobalStateContext";
import { getListCartInfo } from "../../../services/purchaseApi";
import ModalChatCustomer from "../../chat/ModalChatCustomer";
import ErrorPopup from "../../../components/common/ErrorPopup";
import useScrollDetection from "../../../hooks/useScrollDetection";
import { IoArrowBack } from "react-icons/io5";
import { HiOutlineShoppingCart } from "react-icons/hi2";
import { listProduct } from "../../../services/productApi";
import useDebounce from "../../../hooks/useDebounce";
import ProductCardNew from "./ProductCardNew";
import { LuFilter } from "react-icons/lu";
import { IoClose } from "react-icons/io5";
import { BiSortAlt2 } from "react-icons/bi";
import NotFound from "../../../assets/notFound.png";
import SwipeSortBy from "../../../components/swipeComp/SwipeSortBy";
import SwipeFilterBy from "../../../components/swipeComp/SwipeFilterBy";

const FETCH_LIMIT = 12;

const useIntersectionObserver = (callback) => {
  const observerRef = useRef(null);
  const sentinelRef = useCallback(
    (node) => {
      if (observerRef.current) observerRef.current.disconnect();
      if (!node) return;
      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) callback();
        },
        { threshold: 0.1 },
      );
      observerRef.current.observe(node);
    },
    [callback],
  );
  return sentinelRef;
};

const ProductList = () => {
  const isScrolled = useScrollDetection(5);
  const { recipeId, setRecipeId, isLogin } = useContext(GlobalStateContext);
  const { search } = useParams();
  const navigate = useNavigate();
  const machineId = localStorage.getItem("MachineId");

  const [searchKeyword, setSearchKeyword] = useState(search || "");
  const [products, setProducts] = useState([]);
  const [offset, setOffset] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [openModalChatRecipe, setOpenModalChatRecipe] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [companyId, setCompanyId] = useState(
    localStorage.getItem("companyId") || "",
  );
  const [addToCartProduct, setAddToCartProduct] = useState({
    company_id: 0,
    price: 0,
    product_id: 0,
    recipe_id: recipeId,
    product_name: "",
    image: [],
    product_type: "",
    stock: 0,
    quantity: 0,
    prescription_image: [],
    check_state: true,
    uuid: machineId,
  });
  const [filterSort, setFilterSort] = useState({
    category_id: 0,
    higher_price: 0,
    lower_price: 0,
    sort_by: 0,
  });
  const [showSortByPicker, setShowSortByPicker] = useState(false);
  const [showFilterByPicker, setShowFilterByPicker] = useState(false);

  const debounceSearch = useDebounce(searchKeyword, 1000);

  const reqBody = {
    company_id: companyId ? Number(companyId) : 0,
    limit: FETCH_LIMIT,
    offset: 1,
    keyword: debounceSearch || "",
    ...filterSort,
  };

  const dummyProducts = Array(6).fill({
    id: 0,
    name: "",
    media: [],
    price: 0,
    discount_type: 0,
    discount_price: null,
    discount: null,
    stock: 0,
    quantity: 0,
    get_product_id: 0,
  });

  useEffect(() => {
    setAddToCartProduct((p) => ({ ...p, recipe_id: recipeId }));
  }, [recipeId]);

  const { data: countCartData, isLoading: isCountCartLoading } = useQuery({
    queryKey: ["count-cart"],
    queryFn: () => getListCartInfo(),
    enabled: !isLogin,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  const { mutate: mutateList, isPending: isListLoading } = useMutation({
    mutationFn: listProduct,
    onSuccess: (data, variables) => {
      if (!data || data.length === 0) {
        setHasMore(false);
        if (variables.offset === 1) setProducts([]);
        return;
      }
      if (variables.offset === 1) {
        setProducts(data.map((d) => ({ ...d, isLoading: false })));
      } else {
        setProducts((prev) => [
          ...prev,
          ...data.map((d) => ({ ...d, isLoading: false })),
        ]);
      }
      setHasMore(data.length === FETCH_LIMIT);
      setIsLoadingMore(false);
    },
    onError: () => setIsLoadingMore(false),
  });

  useEffect(() => {
    setOffset(1);
    setHasMore(true);
    setProducts([]);
    mutateList({
      company_id: companyId ? Number(companyId) : 0,
      limit: FETCH_LIMIT,
      offset: 1,
      keyword: debounceSearch || "",
      ...filterSort,
    });
  }, [debounceSearch, filterSort, companyId]);

  const loadMore = useCallback(() => {
    if (isListLoading || isLoadingMore || !hasMore) return;
    const nextOffset = offset + 1;
    setOffset(nextOffset);
    setIsLoadingMore(true);
    mutateList({
      company_id: companyId ? Number(companyId) : 0,
      limit: FETCH_LIMIT,
      offset: nextOffset,
      keyword: debounceSearch || "",
      ...filterSort,
    });
  }, [
    isListLoading,
    isLoadingMore,
    hasMore,
    offset,
    companyId,
    debounceSearch,
    filterSort,
  ]);

  const sentinelRef = useIntersectionObserver(loadMore);

  const handleSetReqBody = (newReqBody) => {
    setFilterSort({
      category_id: newReqBody.category_id ?? filterSort.category_id,
      higher_price: newReqBody.higher_price ?? filterSort.higher_price,
      lower_price: newReqBody.lower_price ?? filterSort.lower_price,
      sort_by: newReqBody.sort_by ?? filterSort.sort_by,
    });
  };

  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === "companyId") setCompanyId(e.newValue || "");
    };
    window.addEventListener("storage", handleStorage);
    const observer = new MutationObserver(() => {
      setCompanyId(localStorage.getItem("companyId") || "");
    });
    observer.observe(document, {
      attributes: true,
      childList: true,
      subtree: true,
    });
    return () => {
      window.removeEventListener("storage", handleStorage);
      observer.disconnect();
    };
  }, []);

  return (
    <div className="bg-white relative">
      <div className="flex flex-row justify-center h-screen">
        <div className="w-full lg:w-1/2 flex justify-center lg:items-center px-0 lg:px-12 text-base">
          <div className="rounded-md py-4 px-3 lg:p-8 w-full h-fit">
            <div
              className={`bg-white fixed z-[9999] top-0 left-0 px-4 py-3 w-full flex items-center transition-all duration-300 ${
                isScrolled ? "shadow-md rounded-b-3xl" : ""
              }`}
            >
              <div className="flex flex-col w-full gap-4">
                <div className="w-full flex items-center gap-2 lg:hidden text-xl">
                  <div
                    onClick={() => navigate(-1)}
                    className="p-1 hover:bg-gray-200 rounded-full"
                  >
                    <IoArrowBack className="text-gray-500" size={20} />
                  </div>
                  <TextField
                    autoComplete="off"
                    fullWidth
                    placeholder="Cari Produk"
                    value={searchKeyword}
                    onChange={(e) => {
                      setSearchKeyword(e.target.value);
                      setRecipeId(null);
                    }}
                    InputProps={{
                      sx: {
                        backgroundColor: "white",
                        fontSize: "14px",
                        borderRadius: "32px",
                        height: "40px",
                        fontFamily: '"Montserrat", sans-serif',
                        "& .MuiInputBase-input": {
                          color: "#111827",
                          fontSize: { xs: "14px", lg: "16px" },
                          fontFamily: '"Montserrat", sans-serif',
                          "&::placeholder": {
                            color: "#CBCBCB",
                            opacity: 1,
                            fontFamily: '"Montserrat", sans-serif',
                          },
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#e5e7eb",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#d1d5db",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#94c679",
                        },
                      },
                    }}
                    sx={{
                      backgroundColor: "#fff",
                      "& ::placeholder": { fontSize: "14px" },
                      fontSize: "14px",
                      height: "40px",
                      borderRadius: "32px",
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "32px",
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#94c679",
                        },
                      },
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && searchKeyword !== "") {
                        navigate(`/purchase/search-product/${searchKeyword}`);
                      }
                    }}
                  />
                  <div className="relative">
                    <HiOutlineShoppingCart
                      size={28}
                      className="cursor-pointer hover:text-gray-700 text-gray-500"
                      onClick={() => navigate("/purchase/cart-details")}
                    />
                    {countCartData && countCartData.count > 0 && (
                      <div className="absolute -right-2 -top-2 text-sm h-4 w-4 rounded-full flex items-center justify-center bg-error text-white">
                        {countCartData && !isCountCartLoading
                          ? countCartData.count
                          : "x"}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="w-full">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-1">
                  <div
                    onClick={() => setShowFilterByPicker(true)}
                    className={`border ${filterSort.category_id !== 0 || filterSort.lower_price !== 0 || filterSort.higher_price !== 0 ? "bg-secondaryActive border-secondary text-secondary" : "bg-slate-100 border-slate-100 text-slate-500"} py-2 px-3 rounded-full flex items-center gap-1`}
                  >
                    <p className="text-md font-medium">Filter</p>
                    <LuFilter size={16} />
                  </div>
                  <div
                    onClick={() => setShowSortByPicker(true)}
                    className={`border ${filterSort.sort_by !== 0 ? "bg-secondaryActive border-secondary text-secondary" : "bg-slate-100 border-slate-100 text-slate-500"} py-2 px-3 rounded-full flex items-center gap-1`}
                  >
                    <p className="text-md font-medium">Urutan</p>
                    <BiSortAlt2 size={18} />
                  </div>
                </div>
                {filterSort.category_id !== 0 ||
                filterSort.lower_price !== 0 ||
                filterSort.higher_price !== 0 ||
                filterSort.sort_by !== 0 ? (
                  <div
                    onClick={() =>
                      setFilterSort({
                        category_id: 0,
                        higher_price: 0,
                        lower_price: 0,
                        sort_by: 0,
                      })
                    }
                    className="bg-slate-100 text-slate-500 p-2 rounded-full cursor-pointer"
                  >
                    <IoClose size={16} />
                  </div>
                ) : null}
              </div>

              <div className="mb-4">
                <div
                  className={`w-full grid ${products.length === 0 && !isListLoading ? "grid-cols-1" : "grid-cols-2"} sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2`}
                >
                  {isListLoading && products.length === 0 ? (
                    dummyProducts.map((product, i) => (
                      <ProductCardNew key={i} item={product} isLoading={true} />
                    ))
                  ) : products && products.length > 0 ? (
                    products.map((product, i) => (
                      <ProductCardNew
                        key={product.id || i}
                        item={product}
                        isLoading={false}
                      />
                    ))
                  ) : (
                    <div className="min-h-[calc(100vh-150px)] w-full flex items-center justify-center">
                      <div className="flex flex-col items-center justify-center text-slate-700 px-4">
                        <img
                          src={NotFound}
                          alt="Not Found"
                          className="w-1/2 mb-3"
                        />
                        <h1 className="font-semibold text-lg">
                          Tidak Ada Hasil Yang Sesuai
                        </h1>
                        <p className="text-center text-md mt-2">
                          Gunakan kata kuncimu untuk menemukan produk yang
                          lainnya
                        </p>
                        <button
                          type="button"
                          onClick={() => setSearchKeyword("")}
                          className="h-[40px] text-base mt-3 border border-secondary flex items-center justify-center bg-secondary text-white w-full tracking-wide font-medium rounded-lg focus:outline-none focus:shadow-outline hover:bg-secondaryActive transform duration-200 hover:scale-100"
                        >
                          Ubah Pencarian
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {hasMore && products.length > 0 && (
                  <div
                    ref={sentinelRef}
                    className="w-full flex justify-center py-4"
                  >
                    {isLoadingMore && <CircularProgress size={24} />}
                  </div>
                )}
              </div>

              <ModalChatCustomer
                openChat={openModalChatRecipe}
                setOpenChat={setOpenModalChatRecipe}
                drugData={addToCartProduct}
              />
              <ErrorPopup
                errorMessage={errorMessage}
                setErrorMessage={setErrorMessage}
              />
            </div>
          </div>
        </div>
      </div>

      <SwipeSortBy
        open={showSortByPicker}
        onClose={() => setShowSortByPicker(false)}
        setReqBody={handleSetReqBody}
        reqBody={reqBody}
      />
      <SwipeFilterBy
        open={showFilterByPicker}
        onClose={() => setShowFilterByPicker(false)}
        setReqBody={handleSetReqBody}
        reqBody={reqBody}
      />
    </div>
  );
};

export default ProductList;
